import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { previewContent } from "@/lib/content-ops.functions";
import { isContentKind, type ContentKindId } from "@/lib/content-ops";

export const Route = createFileRoute("/_authenticated/admin/preview/$kind/$id")({
  head: () => ({ meta: [{ title: "Content preview · Admin" }, { name: "robots", content: "noindex" }] }),
  component: PreviewPage,
});

function PreviewPage() {
  const { kind, id } = Route.useParams();
  const preview = useServerFn(previewContent);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isContentKind(kind)) { setError("Unknown content kind"); return; }
    preview({ data: { kind: kind as ContentKindId, id } })
      .then(setData)
      .catch((e) => setError((e as Error).message));
  }, [kind, id]);

  if (error) return <p className="mx-auto max-w-3xl px-4 py-16 text-sm text-red-400">{error}</p>;
  if (!data) return <p className="mx-auto max-w-3xl px-4 py-16 text-sm text-muted-foreground">Loading preview…</p>;

  const s = data.summary;
  const row = JSON.parse(data.rowJson ?? "{}") as Record<string, unknown>;

  return (
    <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Preview · {s.state}</p>
      <h1 className="mt-3 font-serif text-4xl text-gold-luminous">{s.title}</h1>
      {s.description && <p className="mt-3 text-muted-foreground">{s.description}</p>}
      {s.image && (
        <img src={s.image} alt={s.imageAlt ?? ""} loading="lazy" className="mt-6 w-full rounded-lg border hairline" />
      )}

      <section className="mt-8 rounded-lg border hairline p-4">
        <h2 className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Search &amp; social preview</h2>
        <p className="mt-3 text-sm text-gold">{s.path ?? "(no public URL)"}</p>
        <p className="font-serif text-lg">{s.title}</p>
        <p className="text-sm text-muted-foreground">{s.description || "(no description)"}</p>
      </section>

      <section className="mt-6 rounded-lg border hairline p-4">
        <h2 className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Quality checks</h2>
        <ul className="mt-2 space-y-1 text-sm">
          {data.issues.length === 0 && <li className="text-emerald">All checks pass.</li>}
          {data.issues.map((i: any) => (
            <li key={i.code} className={i.level === "error" ? "text-red-400" : i.level === "warn" ? "text-amber-300" : "text-muted-foreground"}>
              [{i.level}] {i.message}
            </li>
          ))}
        </ul>
      </section>

      <details className="mt-6 rounded-lg border hairline p-4">
        <summary className="cursor-pointer text-xs uppercase tracking-[0.25em] text-muted-foreground">Raw record</summary>
        <pre className="mt-3 overflow-x-auto text-xs text-muted-foreground">{JSON.stringify(row, null, 2)}</pre>
      </details>

      <div className="mt-8 flex gap-3">
        <Link to="/admin/content" className="rounded-full border hairline px-5 py-2.5 text-sm">Back to content ops</Link>
        {s.path && s.state === "published" && (
          <a href={s.path} className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">
            View live page
          </a>
        )}
      </div>
    </article>
  );
}
